import { useState, useEffect, useMemo } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { DashboardGreeting } from "@/components/dashboard/dashboard-greeting";
import { DashboardKpiStats } from "@/components/dashboard/dashboard-kpi-stats";
import { DashboardRecentActivity } from "@/components/dashboard/dashboard-recent-activity";
import { DashboardReminders } from "@/components/dashboard/dashboard-reminders";
import { DashboardInventoryHealth } from "@/components/dashboard/dashboard-inventory-health";
import { DashboardSkeleton } from "@/components/dashboard/dashboard-skeleton";
import { DueRemindersModal } from "@/components/dashboard/due-reminders-modal";
import { QuickDispenseButton } from "@/components/dashboard/quick-dispense-button";
import {
  SetupChecklist,
  createSetupTasks,
} from "@/components/dashboard/setup-checklist";
import { DashboardAnnouncements } from "@/components/dashboard/dashboard-announcements";
import { PharmacyDetailsModal } from "@/components/dashboard/pharmacy-details-modal";
import { PermissionGate } from "@/components/security/permission-gate";
import { useAuth } from "@/lib/auth/auth-provider";
import { usePharmacyStore } from "@/lib/stores/pharmacy-store";
import { usePermission } from "@/hooks/use-permission";
import { useRemindersContext } from "@/context/reminders-context";
import { getActivity } from "@/lib/api/activity";
import { api } from "@/lib/api/client";
import { formatToApiDate } from "@repo/utils";
import type { ActivityTransaction } from "@/lib/types/activity";
import type { Permission } from "@/lib/permissions";
import { useQueryClient } from "@tanstack/react-query";

export const Route = createFileRoute("/_app/dashboard")({
  component: DashboardPage,
});

interface TrendDataPoint {
  date: string;
  receipts: number;
  dispenses: number;
  adjustments: number;
}

interface AnalyticsData {
  trends: TrendDataPoint[];
  topDispensed: unknown[];
  topReceived: unknown[];
  inventoryStatus: {
    in_stock: number;
    reorder_needed: number;
    sold_out: number;
  };
  totalItems: number;
}

function DashboardPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const pharmacy = usePharmacyStore((s) => s.pharmacy);
  const users = usePharmacyStore((s) => s.users);

  const { isAllowed: canViewAnalytics, isLoading: permLoading } = usePermission(
    "analytics:view" as Permission
  );
  const { isAllowed: canViewActivity } = usePermission(
    "activity:view" as Permission
  );

  // Reminders from centralized context
  const {
    reminders: allReminders,
    isLoading: remindersLoading,
    refetch: refetchReminders,
  } = useRemindersContext();

  // Dashboard data state
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(
    null
  );
  const [activityData, setActivityData] = useState<ActivityTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activityLoading, setActivityLoading] = useState(true);
  const [showPharmacyModal, setShowPharmacyModal] = useState(false);

  const pharmacyName = auth.pharmacy?.name || "";
  const userName = auth.user?.name || "";

  // Setup checklist logic — DEA number is the only required compliance field
  const hasPharmacyDetails = Boolean(pharmacy?.dea_registration_number);
  const hasMultipleUsers = users.length > 1;
  const isAdmin = auth.user?.role === "admin";
  const showSetupChecklist =
    isAdmin && (!hasPharmacyDetails || users.length <= 1);

  const setupTasks = useMemo(
    () =>
      createSetupTasks({
        hasUsers: hasMultipleUsers,
        hasPharmacyDetails,
        onAddUsers: () => navigate({ to: "/users" }),
        onUpdatePharmacyDetails: () => setShowPharmacyModal(true),
      }),
    [hasMultipleUsers, hasPharmacyDetails, navigate]
  );
  const setupCompletedCount = setupTasks.filter((t) => t.completed).length;

  // Extract today's stats from analytics trends
  const todayStr = formatToApiDate(new Date());
  const todayTrend = analyticsData?.trends?.find((t) => t.date === todayStr);
  const todayDispenses = todayTrend?.dispenses ?? 0;
  const todayReceipts = todayTrend?.receipts ?? 0;

  // Split reminders: due (overdue + today) vs all active (for list)
  const dueReminders = useMemo(
    () => allReminders.filter((r) => !r.is_completed && r.due_date <= todayStr),
    [allReminders, todayStr]
  );

  const activeReminders = useMemo(
    () => allReminders.filter((r) => !r.is_completed),
    [allReminders]
  );

  // Parallel data fetching
  useEffect(() => {
    if (permLoading) return;

    let cancelled = false;

    async function loadDashboardData() {
      const promises: Promise<void>[] = [];

      // Conditionally fetch analytics
      if (canViewAnalytics) {
        promises.push(
          (async () => {
            try {
              const endDate = new Date();
              const startDate = new Date();
              startDate.setDate(endDate.getDate() - 7);

              const raw = await api.get<
                { data?: AnalyticsData } & AnalyticsData
              >("/analytics", {
                params: {
                  startDate: formatToApiDate(startDate),
                  endDate: formatToApiDate(endDate),
                },
              });
              if (cancelled) return;
              setAnalyticsData(raw.data || raw);
            } catch {
              // Analytics are non-critical
            }
          })()
        );
      }

      // Conditionally fetch activity
      if (canViewActivity) {
        promises.push(
          (async () => {
            try {
              const raw = await getActivity({ pageSize: 8, page: 1 });
              if (cancelled) return;
              const result =
                (raw as { data?: { transactions?: ActivityTransaction[] } })
                  .data ?? raw;
              const txns =
                (result as { transactions?: ActivityTransaction[] })
                  .transactions ?? [];
              setActivityData(txns);
            } catch {
              // Activity is non-critical
            } finally {
              if (!cancelled) setActivityLoading(false);
            }
          })()
        );
      } else {
        setActivityLoading(false);
      }

      await Promise.allSettled(promises);
      if (!cancelled) setIsLoading(false);
    }

    loadDashboardData();
    return () => {
      cancelled = true;
    };
  }, [permLoading, canViewAnalytics, canViewActivity]);

  if (isLoading || permLoading) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="space-y-6" data-testid="dashboard-root">
      {/* Blocking modal for due reminders */}
      <DueRemindersModal
        reminders={dueReminders}
        onReminderChanged={refetchReminders}
      />

      {/* Header: Greeting + Quick Dispense */}
      <div className="flex items-center justify-between">
        <DashboardGreeting name={userName || pharmacyName} />
        <QuickDispenseButton />
      </div>

      {/* KPI Stats */}
      <DashboardKpiStats
        totalItems={analyticsData?.totalItems ?? null}
        todayDispenses={todayDispenses}
        todayReceipts={todayReceipts}
        remindersDue={dueReminders.length}
      />

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Main column */}
        <div className="lg:col-span-8 space-y-6">
          {/* Reminders — always visible */}
          <DashboardReminders
            reminders={activeReminders}
            isLoading={remindersLoading}
          />

          {/* Recent activity — permission-gated */}
          <PermissionGate permission={"activity:view" as Permission}>
            <DashboardRecentActivity
              transactions={activityData}
              isLoading={activityLoading}
            />
          </PermissionGate>
        </div>

        {/* Sidebar column */}
        <div className="lg:col-span-4 space-y-6">
          {/* Announcements */}
          <DashboardAnnouncements />

          {/* Setup checklist — only if incomplete */}
          {showSetupChecklist && (
            <SetupChecklist
              tasks={setupTasks}
              completedCount={setupCompletedCount}
              totalCount={setupTasks.length}
              variant="minimal"
            />
          )}

          {/* Inventory health — permission-gated */}
          <PermissionGate permission={"analytics:view" as Permission}>
            <DashboardInventoryHealth
              status={analyticsData?.inventoryStatus ?? null}
              totalItems={analyticsData?.totalItems ?? null}
              isLoading={false}
            />
          </PermissionGate>
        </div>
      </div>

      {/* Pharmacy Details Modal */}
      <PharmacyDetailsModal
        open={showPharmacyModal}
        onClose={() => setShowPharmacyModal(false)}
        onSuccess={() => queryClient.invalidateQueries()}
        pharmacyName={pharmacyName}
        currentDetails={{
          ncpdp_id: pharmacy?.ncpdp_id as string | null | undefined,
          dea_registration_number: pharmacy?.dea_registration_number as
            | string
            | null
            | undefined,
          dea_expiration_date: pharmacy?.dea_expiration_date as
            | string
            | null
            | undefined,
          state_license_number: pharmacy?.state_license_number as
            | string
            | null
            | undefined,
          license_expiration_date: pharmacy?.license_expiration_date as
            | string
            | null
            | undefined,
        }}
      />
    </div>
  );
}
